import { useMemo, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';
import { Button } from './Button';
import { EmptyState, ErrorState, Skeleton } from './feedback';

export interface Column<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Providing this makes the column sortable. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'left' | 'right' | 'center';
  className?: string;
  headerClassName?: string;
  /** Hide the column below this breakpoint. */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl';
}

export interface DataTableProps<T> {
  /** Accessible table name, e.g. "Residents". Not visible. */
  caption: string;
  columns: readonly Column<T>[];
  /** undefined while loading. */
  rows: readonly T[] | undefined;
  getRowId: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Shown when there are no rows. Use <EmptyState> with a next-step action. */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  selectedId?: string | null;
  /** Rows per page; 0 disables pagination. Default 10. */
  pageSize?: number;
  initialSort?: { columnId: string; direction: 'asc' | 'desc' };
  /** Rendered instead of the table below the md breakpoint (cards, per the design system). */
  mobileCard?: (row: T) => ReactNode;
  className?: string;
}

const hideClass = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
} as const;

const alignClass = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

function compare(a: string | number | null | undefined, b: string | number | null | undefined): number {
  if (a === b) {
    return 0;
  }
  if (a === null || a === undefined) {
    return 1;
  }
  if (b === null || b === undefined) {
    return -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' });
}

/**
 * The standard list view: sortable headers, client-side pagination, skeleton rows while
 * loading, an error state with retry, an empty state with a next step, keyboard-reachable
 * rows, and a card layout on small screens.
 */
export function DataTable<T>({
  caption,
  columns,
  rows,
  getRowId,
  loading,
  error,
  onRetry,
  empty,
  onRowClick,
  selectedId,
  pageSize = 10,
  initialSort,
  mobileCard,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState(initialSort ?? null);
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    if (!rows) {
      return [];
    }
    const column = sort ? columns.find((c) => c.id === sort.columnId) : undefined;
    if (!sort || !column?.sortValue) {
      return [...rows];
    }
    const sortValue = column.sortValue;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => factor * compare(sortValue(a), sortValue(b)));
  }, [rows, columns, sort]);

  const paged = pageSize > 0;
  const pageCount = paged ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount - 1);
  const visible = paged ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize) : sorted;

  const toggleSort = (columnId: string): void => {
    setPage(0);
    setSort((current) => {
      if (current?.columnId !== columnId) {
        return { columnId, direction: 'asc' };
      }
      return { columnId, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  if (error && !rows) {
    return (
      <div className={cn('rounded-card border border-slate-200 bg-white shadow-card', className)}>
        <ErrorState error={error} onRetry={onRetry} compact />
      </div>
    );
  }

  const showSkeleton = loading || !rows;
  const isEmpty = !showSkeleton && sorted.length === 0;

  const activateRow = (row: T) => ({
    onClick: (event: MouseEvent) => {
      // Clicks on buttons/links inside the row keep their own behaviour.
      if ((event.target as HTMLElement).closest('a,button,input,select,textarea,label')) {
        return;
      }
      onRowClick?.(row);
    },
    onKeyDown: (event: KeyboardEvent) => {
      if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onRowClick?.(row);
      }
    },
  });

  return (
    <div className={cn('overflow-hidden rounded-card border border-slate-200 bg-white shadow-card', className)}>
      {/* Phone/small: cards */}
      {mobileCard && !showSkeleton && !isEmpty && (
        <ul className="divide-y divide-slate-100 md:hidden">
          {visible.map((row) => (
            <li key={getRowId(row)}>
              {onRowClick ? (
                <button type="button" onClick={() => onRowClick(row)} className="block w-full px-4 py-3.5 text-left hover:bg-slate-50">
                  {mobileCard(row)}
                </button>
              ) : (
                <div className="px-4 py-3.5">{mobileCard(row)}</div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className={cn('scroll-thin overflow-x-auto', mobileCard && !showSkeleton && !isEmpty && 'hidden md:block')}>
        <table className="w-full min-w-full border-collapse text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80">
              {columns.map((column) => {
                const sortable = Boolean(column.sortValue);
                const active = sort?.columnId === column.id;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(
                      'whitespace-nowrap px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500',
                      alignClass[column.align ?? 'left'],
                      column.hideBelow && hideClass[column.hideBelow],
                      column.headerClassName,
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column.id)}
                        className={cn('-mx-1 inline-flex items-center gap-1 rounded px-1 uppercase tracking-wide hover:text-slate-900', active && 'text-slate-900')}
                      >
                        {column.header}
                        {active ? sort?.direction === 'asc' ? <ArrowUp className="size-3" aria-hidden /> : <ArrowDown className="size-3" aria-hidden /> : <ChevronsUpDown className="size-3 opacity-50" aria-hidden />}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {showSkeleton &&
              Array.from({ length: paged ? Math.min(pageSize, 6) : 6 }, (_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`}>
                  {columns.map((column) => (
                    <td key={column.id} className={cn('px-4 py-3.5', column.hideBelow && hideClass[column.hideBelow])}>
                      <Skeleton className="h-4 w-full max-w-40" />
                    </td>
                  ))}
                </tr>
              ))}
            {!showSkeleton &&
              visible.map((row) => {
                const id = getRowId(row);
                const interactive = Boolean(onRowClick);
                return (
                  // Mouse users click anywhere on the row; keyboard users Tab to the row and press Enter.
                  <tr
                    key={id}
                    tabIndex={interactive ? 0 : undefined}
                    aria-selected={selectedId === id || undefined}
                    className={cn(
                      'transition-colors',
                      interactive && 'cursor-pointer hover:bg-slate-50 focus-visible:bg-slate-50',
                      selectedId === id && 'bg-amethyst-50/60',
                    )}
                    {...(interactive ? activateRow(row) : {})}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.id}
                        className={cn(
                          'px-4 py-3 align-middle text-slate-700',
                          alignClass[column.align ?? 'left'],
                          column.hideBelow && hideClass[column.hideBelow],
                          column.className,
                        )}
                      >
                        {column.cell(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {isEmpty && (empty ?? <EmptyState title="Nothing here yet" />)}

      {paged && !showSkeleton && sorted.length > pageSize && (
        <Pagination page={safePage} pageCount={pageCount} total={sorted.length} pageSize={pageSize} onPageChange={setPage} />
      )}
    </div>
  );
}

export interface PaginationProps {
  /** Zero-based. */
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, pageCount, total, pageSize, onPageChange }: PaginationProps) {
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <nav aria-label="Pagination" className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
      <p className="tabular">
        Showing <span className="font-semibold text-slate-700">{formatNumber(from)}–{formatNumber(to)}</span> of {formatNumber(total)}
      </p>
      <div className="flex items-center gap-1.5">
        <Button variant="secondary" size="sm" aria-label="Previous page" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft className="size-4" aria-hidden />
        </Button>
        <span className="tabular px-1">
          Page {page + 1} of {pageCount}
        </span>
        <Button variant="secondary" size="sm" aria-label="Next page" disabled={page >= pageCount - 1} onClick={() => onPageChange(page + 1)}>
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      </div>
    </nav>
  );
}
